import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import api, { resolveImage, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Loader2, X, Upload, Plus, Trash2 } from "lucide-react";

const PLATFORMS = ["instagram", "facebook", "tiktok", "xhs", "wechat", "whatsapp", "other"];

export default function AdminAngelina() {
  const { t } = useTranslation();
  const [form, setForm] = useState({
    bio_zh: "", bio_en: "", photos: [], endorsed_product_ids: [], social_links: [],
  });
  const [products, setProducts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/angelina").then((r) => setForm({
      bio_zh: r.data.bio_zh || "", bio_en: r.data.bio_en || "",
      photos: r.data.photos || [], endorsed_product_ids: r.data.endorsed_product_ids || [],
      social_links: r.data.social_links || [],
    }));
    api.get("/products").then((r) => setProducts(r.data));
  }, []);

  const inputCls = "w-full px-4 py-2.5 rounded-md border border-[#e5ebeb] focus:border-[#00868e] outline-none bg-white";

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch("/admin/angelina", {
        ...form,
        social_links: form.social_links.filter((s) => s.url),
      });
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); } finally { setSaving(false); }
  };

  const uploadPhoto = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/upload/angelina-photo", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      // Append the new photo. First photo remains the main.
      setForm((f) => ({ ...f, photos: [...f.photos, data.path] }));
      toast.success(t("common.saved"));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setUploading(false); e.target.value = ""; }
  };

  const toggleProduct = (id) => {
    setForm((f) => ({
      ...f,
      endorsed_product_ids: f.endorsed_product_ids.includes(id)
        ? f.endorsed_product_ids.filter((x) => x !== id)
        : [...f.endorsed_product_ids, id],
    }));
  };

  const addSocial = () =>
    setForm((f) => ({ ...f, social_links: [...f.social_links, { platform: "instagram", url: "", label: "" }] }));
  const updateSocial = (i, patch) =>
    setForm((f) => ({
      ...f,
      social_links: f.social_links.map((s, j) => (i === j ? { ...s, ...patch } : s)),
    }));
  const removeSocial = (i) =>
    setForm((f) => ({ ...f, social_links: f.social_links.filter((_, j) => j !== i) }));

  return (
    <form onSubmit={submit} className="p-6 md:p-10 max-w-4xl" data-testid="admin-angelina-page">
      <h1 className="font-serif-cn text-3xl text-[#003645] font-semibold">{t("adminP3.angelinaCms")}</h1>

      <div className="mt-6 rounded-lg border border-[#e5ebeb] bg-white p-6 space-y-4">
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#4a6e77]">Bio ZH</label>
            <textarea rows={6} value={form.bio_zh} onChange={(e) => setForm({ ...form, bio_zh: e.target.value })} className={inputCls} data-testid="angelina-bio-zh" />
          </div>
          <div>
            <label className="text-xs text-[#4a6e77]">Bio EN</label>
            <textarea rows={6} value={form.bio_en} onChange={(e) => setForm({ ...form, bio_en: e.target.value })} className={inputCls} />
          </div>
        </div>

        {/* Photos */}
        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#4a6e77]">{t("masters.photo")} — 第一张为主图 / First one is the hero</div>
            <label className="inline-flex items-center gap-2 px-3 py-1.5 rounded-md border border-[#e5ebeb] text-xs text-[#003645] hover:border-[#00868e] cursor-pointer" data-testid="angelina-photo-upload-label">
              {uploading ? <Loader2 size={12} className="animate-spin" /> : <Upload size={12} />}
              {t("admin.uploadImage")}
              <input type="file" accept="image/*" className="hidden" onChange={uploadPhoto} disabled={uploading} data-testid="angelina-photo-upload-input" />
            </label>
          </div>
          <div className="mt-3 flex flex-wrap gap-3" data-testid="angelina-photos-list">
            {form.photos.map((p, i) => (
              <div key={i} className="relative h-28 w-28 rounded-md overflow-hidden border border-[#e5ebeb] group" data-testid={`angelina-photo-${i}`}>
                <img src={resolveImage(p)} alt="" className="h-full w-full object-cover" />
                {i === 0 && (
                  <span className="absolute top-1 left-1 text-[10px] px-2 py-0.5 rounded-full bg-[#005870] text-white font-semibold">MAIN</span>
                )}
                <div className="absolute inset-x-0 bottom-0 bg-[#003645]/70 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex justify-center gap-1 py-1">
                  {i !== 0 && (
                    <button
                      type="button"
                      onClick={() => setForm((f) => {
                        const arr = [...f.photos];
                        [arr[0], arr[i]] = [arr[i], arr[0]];
                        return { ...f, photos: arr };
                      })}
                      className="text-[10px] text-white px-2 py-0.5 rounded-full bg-white/20 hover:bg-white/30"
                      data-testid={`angelina-photo-make-main-${i}`}
                    >
                      Set main
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={() => setForm((f) => ({ ...f, photos: f.photos.filter((_, j) => j !== i) }))}
                    className="text-[10px] text-white px-2 py-0.5 rounded-full bg-red-500/80 hover:bg-red-500"
                    data-testid={`angelina-photo-remove-${i}`}
                  >
                    Remove
                  </button>
                </div>
              </div>
            ))}
            {form.photos.length === 0 && (
              <div className="text-xs text-[#4a6e77]">尚无照片 / No photos yet</div>
            )}
          </div>
        </div>

        {/* Social links */}
        <div>
          <div className="flex items-center justify-between">
            <div className="text-xs text-[#4a6e77]">{t("social.title")}</div>
            <button type="button" onClick={addSocial} className="inline-flex items-center gap-1 text-xs text-[#005870] hover:text-[#00868e]" data-testid="add-social-link-btn">
              <Plus size={12} /> Add
            </button>
          </div>
          <div className="mt-2 space-y-2" data-testid="angelina-social-links">
            {form.social_links.map((s, i) => (
              <div key={i} className="flex flex-col sm:flex-row gap-2" data-testid={`social-row-${i}`}>
                <select value={s.platform} onChange={(e) => updateSocial(i, { platform: e.target.value })} className={inputCls + " sm:w-40"}>
                  {PLATFORMS.map((p) => <option key={p} value={p}>{t(`social.platforms.${p}`)}</option>)}
                </select>
                <input placeholder="https://..." value={s.url} onChange={(e) => updateSocial(i, { url: e.target.value })} className={inputCls + " flex-1"} data-testid={`social-url-${i}`} />
                <input placeholder="Label (optional)" value={s.label || ""} onChange={(e) => updateSocial(i, { label: e.target.value })} className={inputCls + " sm:w-48"} />
                <button type="button" onClick={() => removeSocial(i)} className="h-10 w-10 flex items-center justify-center rounded-md border border-[#e5ebeb] text-red-600 hover:border-red-200" data-testid={`remove-social-${i}`}>
                  <Trash2 size={14} />
                </button>
              </div>
            ))}
          </div>
        </div>

        {/* Endorsed products */}
        <div>
          <div className="text-xs text-[#4a6e77] mb-2">Endorsed products</div>
          <div className="max-h-64 overflow-y-auto border border-[#e5ebeb] rounded-md p-2 space-y-1" data-testid="endorsed-products-picker">
            {products.map((p) => (
              <label key={p.id} className="flex items-center gap-2 py-1 hover:bg-[#fdfbf7] rounded px-1">
                <input type="checkbox" checked={form.endorsed_product_ids.includes(p.id)} onChange={() => toggleProduct(p.id)} />
                <span className="text-sm text-[#003645] flex-1">{p.name_zh} / {p.name_en}</span>
              </label>
            ))}
          </div>
        </div>
      </div>

      <button type="submit" disabled={saving} className="mt-6 inline-flex items-center gap-2 px-6 py-2.5 rounded-full bg-[#005870] text-white font-medium hover:bg-[#003645] disabled:opacity-60" data-testid="angelina-save-btn">
        {saving && <Loader2 size={14} className="animate-spin" />}{t("common.save")}
      </button>
    </form>
  );
}
